import {
  AudioItemDetail,
  FetchChaptersFnReturnType,
  StrictOmit,
} from '@listening/api-contract/legacy-client-types';
import { useContextAndErrorIfNull } from '@listening/utils/react-helpers';
import type {
  AppLogger,
  LogSettings,
} from '@listening/frontend/logging/app-logger';
import {
  type LegacyChapterChunkState,
  useDebouncedAnchors,
  useLegacyChapterChunks,
} from '@listening/frontend/legacy-reader/use-legacy-chapter-chunks';
import type React from 'react';
import {
  createContext,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAudioChunkSelector } from '../audio-chunk-player';
import type { CachedChapterChunks } from './chapter-cache';
import { legacyChapterChunkReader } from './legacy-chapter-chunks';

export { LEGACY_CHAPTER_CHUNK_SIZE as CHAPTER_CHUNK_SIZE } from '@listening/frontend/legacy-reader/chapter-chunks';

export type DocReaderProps = {
  documentSummary: AudioItemDetail;
  autoScroll: boolean;
  skipWordHighlighting?: boolean | undefined;
  logSettings: LogSettings;
  children: React.ReactNode;
};

type DocReaderContextType = StrictOmit<
  DocReaderProps,
  'logSettings' | 'children'
> & {
  logger: AppLogger;
  /** Per-chapter paragraph counts: the summary's, replaced by the real count
   * once a chapter has loaded and kept after its chunk leaves the cache. */
  paragraphCounts: ReadonlyArray<number>;
  /** Chunk data the client holds, shown or not. */
  cachedChunks: CachedChapterChunks;
};

export type DocReaderViewHandle = {
  scrollToChapter: (chapterId: string) => void;
  scrollToTextChunk: (chunkId: string) => void;
  scrollToIndex: (scrollableIdx: number) => void;
  scrollToTop: () => void;
  scrollToBottom: () => void;
  scrollToCurrent: () => void;
};

export type ChapterChunkQueryResult =
  LegacyChapterChunkState<FetchChaptersFnReturnType>;

export type DocReaderCommonProps = {
  onIsActiveVisibleChange?: (isActiveVisible: boolean) => void;
  scrollContainerClassName?: string;
  scrollHeader?: React.ReactNode;
  scrollFooter?: React.ReactNode;
} & CommonScrollOptions;

export type CommonScrollOptions = {
  overscan?: number;
};

export const DocReaderContext = createContext<DocReaderContextType | null>(
  null,
);

export const SCROLL_TO_UNLOADED_CHUNK_TIMEOUT_MS = 3000;

export const useCore = (props: {
  onIsActiveVisibleChange?: ((isActiveVisible: boolean) => void) | undefined;
  chapterFetchingDisabled?: boolean;
}) => {
  const [isActiveVisible, setIsActiveVisible] = useState(false);
  // Depend on the callback, not the whole fresh `props` object (which changes
  // every render and defeats the memo).
  const onIsActiveVisibleChange = props.onIsActiveVisibleChange;
  const handleIsActiveVisibleChange = useCallback(
    (val: boolean) => {
      setIsActiveVisible(val);
      onIsActiveVisibleChange?.(val);
    },
    [onIsActiveVisibleChange],
  );
  const { documentSummary } = useContextAndErrorIfNull(DocReaderContext);

  // The window follows the playing chapter and the chapters on screen (shared
  // with the RN reader); chunk 0 stays pinned so the SSR-prefetched first page
  // surfaces on hydration.
  const currentChapterId = useAudioChunkSelector((s) => s.chapterId);
  const currentChapterIndex = useMemo(() => {
    const index = documentSummary.chapters.findIndex(
      (chapter) => chapter.id === currentChapterId,
    );
    return index === -1 ? undefined : index;
  }, [currentChapterId, documentSummary.chapters]);
  const visibleAnchors = useDebouncedAnchors<number>();
  const chunks = useLegacyChapterChunks({
    audioId: documentSummary.id,
    chapters: documentSummary.chapters,
    reader: legacyChapterChunkReader,
    enabled: !props.chapterFetchingDisabled,
    currentChapterIndex,
    visibleChapterIndices: visibleAnchors.value,
    alwaysFirstChunk: true,
  });
  const queriesDataStable = useMemo(
    () => chunks.chunkData.map((page) => page?.data),
    [chunks.chunkData],
  );
  const debouncedUpdateViewedChapters = useMemo(
    () => ({
      maybeExecute: visibleAnchors.update,
      flush: visibleAnchors.flush,
    }),
    [visibleAnchors.flush, visibleAnchors.update],
  );

  return {
    queries: chunks.chunkStates,
    queriesDataStable,
    chapterChunksFetchParams: chunks.chunkParams,
    isActiveVisibleState: [
      isActiveVisible,
      handleIsActiveVisibleChange,
    ] as const,
    debouncedUpdateViewedChapters,
  };
};

// The old `useAutoScroll` (pull-back-when-idle scrollIntoView loop) is gone:
// every reader now follows playback through the shared read-along engine
// (`@listening/frontend/read-along/*` + the adapters in
// `reader/follow-adapters.ts`). See
// docs/projects/active/read-along-follow-unification.md.

export function useInitialScroll({
  scrollElement,
  core,
  scrollToChunk,
  ready = true,
}: {
  core: ReturnType<typeof useCore>;
  scrollElement: HTMLElement | null;
  scrollToChunk: (chunkId: string) => void;
  /** Defer until the doc-pack catalog has loaded or failed. */
  ready?: boolean;
}) {
  const scrolledToInitialRef = useRef(false);
  const ttsId = useAudioChunkSelector((s) => s.ttsId);

  const { logger } = useContextAndErrorIfNull(DocReaderContext);
  const initialTtsIdRef = useRef(ttsId);

  useLayoutEffect(() => {
    if (!ready) return;
    if (!scrollElement) return;
    if (!initialTtsIdRef.current) return;
    if (scrolledToInitialRef.current) return;

    logger.info(
      { initialAudioState: initialTtsIdRef.current },
      'Scrolling to initial chunk',
    );

    scrollToChunk(initialTtsIdRef.current);
    scrolledToInitialRef.current = true;
    core.debouncedUpdateViewedChapters.flush();
  }, [
    ready,
    scrollToChunk,
    logger,
    scrollElement,
    core.debouncedUpdateViewedChapters,
  ]);
}
